<script setup>
import GuestLayout from "@/Layouts/GuestLayout.vue";

defineOptions({ layout: GuestLayout });

const stats = [
    { label: "Years experience", value: "20+" },
    { label: "Instrument Types Repaired", value: "Over 20" },
];
</script>

<template>
    <div class="bg-white">
        <main class="isolate">
            <!-- Hero section -->
            <div class="relative isolate -z-10">
                <svg
                    class="absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
                    aria-hidden="true"
                >
                    <defs>
                        <pattern
                            id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
                            width="200"
                            height="200"
                            x="50%"
                            y="-1"
                            patternUnits="userSpaceOnUse"
                        >
                            <path d="M.5 200V.5H200" fill="none" />
                        </pattern>
                    </defs>
                    <svg x="50%" y="-1" class="overflow-visible fill-gray-50">
                        <path
                            d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                            stroke-width="0"
                        />
                    </svg>
                    <rect
                        width="100%"
                        height="100%"
                        stroke-width="0"
                        fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)"
                    />
                </svg>
                <div
                    class="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48"
                    aria-hidden="true"
                >
                    <div
                        class="aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff3b3b] to-[#645afa] opacity-30"
                        style="
                            clip-path: polygon(
                                63.1% 29.5%,
                                100% 17.1%,
                                76.6% 3%,
                                48.4% 0%,
                                44.6% 4.7%,
                                54.5% 25.3%,
                                59.8% 49%,
                                55.2% 57.8%,
                                44.4% 57.2%,
                                27.8% 47.9%,
                                35.1% 81.5%,
                                0% 97.7%,
                                39.2% 100%,
                                35.2% 81.4%,
                                97.2% 52.8%,
                                63.1% 29.5%
                            );
                        "
                    />
                </div>
                <div class="overflow-hidden">
                    <div
                        class="mx-auto max-w-7xl px-6 pb-32 pt-36 sm:pt-60 lg:px-8 lg:pt-32"
                    >
                        <div
                            class="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center"
                        >
                            <div
                                class="w-full max-w-xl lg:shrink-0 xl:max-w-2xl"
                            >
                                <h1
                                    class="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
                                >
                                    We’re changing the way people learn
                                    instrument repair.
                                </h1>
                                <p
                                    class="relative mt-6 text-lg leading-8 text-gray-600 sm:max-w-md lg:max-w-none"
                                >
                                    Discover a revolutionary approach to
                                    education with the Virtual Academy, where
                                    cutting-edge technology meets flexible
                                    learning. Our innovative platform offers the
                                    same high-quality, hands-on education to
                                    students located outside of the Southeast,
                                    breaking down geographical barriers and
                                    bringing top-tier instruction to your
                                    doorstep. Through our unique model, students
                                    attend classes virtually, benefiting from a
                                    dynamic and interactive learning environment
                                    that adapts to their individual needs.
                                </p>
                                <p
                                    class="relative mt-6 text-lg leading-8 text-gray-600 sm:max-w-md lg:max-w-none"
                                >
                                    At the heart of our program is a commitment
                                    to practical, real-world experience. By
                                    partnering with affiliate repair shops, we
                                    ensure that students don’t just learn theory
                                    but also gain invaluable hands-on
                                    experience. As students engage in virtual
                                    classes, their work is meticulously
                                    monitored and inspected by skilled
                                    technicians at our partner repair shops.
                                    This collaboration ensures that every
                                    student receives personalized feedback and
                                    guidance, enhancing their skills and
                                    confidence.
                                </p>
                                <p
                                    class="relative mt-6 text-lg leading-8 text-gray-600 sm:max-w-md lg:max-w-none"
                                >
                                    The Virtual Academy stands out for its
                                    flexibility, allowing students to balance
                                    their education with other commitments.
                                    Whether you’re a working professional, a
                                    parent, or someone seeking a career change,
                                    our adaptable schedules and comprehensive
                                    support system make it possible to achieve
                                    your educational goals without compromising
                                    your lifestyle.
                                </p>
                                <p
                                    class="relative mt-6 text-lg leading-8 text-gray-600 sm:max-w-md lg:max-w-none"
                                >
                                    Join the Virtual Academy today and
                                    experience the future of learning—where
                                    innovative technology, expert instruction,
                                    and practical experience converge to create
                                    an unparalleled educational journey tailored
                                    to your needs.
                                </p>
                            </div>
                            <div
                                class="mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0"
                            >
                                <div
                                    class="ml-auto w-44 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:order-last lg:pt-36 xl:order-none xl:pt-80"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1721918857999-3bf682bc198d?q=80&w=5670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                            alt=""
                                            class="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                                <div
                                    class="mr-auto w-44 flex-none space-y-8 sm:mr-0 sm:pt-52 lg:pt-36"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1629802478579-e0fa4ea66a79?q=80&w=4160&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                            alt=""
                                            class="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                    <div class="relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1701748533596-9d5a56a1cfe7?q=80&w=5472&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                            alt=""
                                            class="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                                <div
                                    class="w-44 flex-none space-y-8 pt-32 sm:pt-0"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1627411187044-cc7a4daaca7d?q=80&w=5472&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                            alt=""
                                            class="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                    <div class="relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1666209932693-d7ce2d71bcdc?q=80&w=3137&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                            alt=""
                                            class="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content section -->
            <div class="mx-auto -mt-12 max-w-7xl px-6 sm:mt-0 lg:px-8 xl:-mt-8">
                <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        Our mission
                    </h2>
                    <div
                        class="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row"
                    >
                        <div class="lg:w-full lg:max-w-2xl lg:flex-auto">
                            <p class="text-xl leading-8 text-gray-600">
                                Band Instrument Repair Academy’s mission is to
                                cultivate the next generation of skilled band
                                instrument repair technicians, elevate the
                                standards of instrument repair services in the
                                industry by providing high-quality training, and
                                serve the musicians in our community through a
                                repair shop for the public.
                            </p>
                            <!--                            <div class="mt-10 max-w-xl text-base leading-7 text-gray-700">-->
                            <!--                                <p>Faucibus commodo massa rhoncus, volutpat. Dignissim sed eget risus enim. Mattis mauris semper sed amet vitae sed turpis id. Id dolor praesent donec est. Odio penatibus risus viverra tellus varius sit neque erat velit. Faucibus commodo massa rhoncus, volutpat. Dignissim sed eget risus enim. Mattis mauris semper sed amet vitae sed turpis id.</p>-->
                            <!--                                <p class="mt-10">Et vitae blandit facilisi magna lacus commodo. Vitae sapien duis odio id et. Id blandit molestie auctor fermentum dignissim. Lacus diam tincidunt ac cursus in vel. Mauris varius vulputate et ultrices hac adipiscing egestas. Iaculis convallis ac tempor et ut. Ac lorem vel integer orci.</p>-->
                            <!--                            </div>-->
                        </div>
                        <div class="lg:flex lg:flex-auto lg:justify-center">
                            <dl class="w-64 space-y-8 xl:w-80">
                                <div
                                    v-for="stat in stats"
                                    :key="stat.label"
                                    class="flex flex-col-reverse gap-y-4"
                                >
                                    <dt
                                        class="text-base leading-7 text-gray-600"
                                    >
                                        {{ stat.label }}
                                    </dt>
                                    <dd
                                        class="text-5xl font-semibold tracking-tight text-gray-900"
                                    >
                                        {{ stat.value }}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Image section -->
            <div class="mt-16 sm:mt-20 xl:mx-auto xl:max-w-7xl xl:px-8">
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <img
                        src="../../../public/fulllogo_text_transparent_nobuffer.webp"
                        alt=""
                        class="h-64 mx-auto"
                    />
                </div>
            </div>

            <!-- Values section -->
            <div class="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 lg:px-8">
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        Our values
                    </h2>
                    <p class="mt-6 text-lg leading-8 text-gray-600">
                        Band Instrument Repair Academy is guided by the values
                        of excellence in craftsmanship, innovation in education,
                        and dedication to community service. The company is
                        committed to providing top-notch repair training with a
                        hands-on approach, utilizing a unique blend of in-house
                        and virtual learning methods. By offering professional
                        repair services to the public, the academy promotes
                        high-quality repairs while actively engaging with the
                        community.
                    </p>
                </div>
            </div>

            <!-- Team section -->
            <div class="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 lg:px-8 pb-16">
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        Our team
                    </h2>
                    <!--                    <p class="mt-6 text-lg leading-8 text-gray-600">Read our bios</p>-->
                </div>
                <ul
                    role="list"
                    class="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-16 text-center sm:grid-cols-3 md:grid-cols-4 lg:mx-0 lg:max-w-none lg:grid-cols-5 xl:grid-cols-6"
                >
                    <li key="gary_gilbert">
                        <img
                            class="mx-auto h-24 w-24 rounded-full"
                            src="../../../public/headshots/gary_gilbert.jpg"
                            alt=""
                        />
                        <h3
                            class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-900"
                        >
                            Gary Gilbert
                        </h3>
                        <p class="text-sm leading-6 text-gray-600">
                            CEO | Lead Instructor | Lead Technician
                        </p>
                    </li>
                    <li key="kristen_howell">
                        <img
                            class="mx-auto h-24 w-24 rounded-full"
                            src="../../../public/headshots/kristen_howell.jpg"
                            alt=""
                        />
                        <h3
                            class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-900"
                        >
                            Kristen Howell
                        </h3>
                        <p class="text-sm leading-6 text-gray-600">
                            General Manager | Program Director
                        </p>
                    </li>
                    <li key="warren_stokes">
                        <img
                            class="mx-auto h-24 w-24 rounded-full"
                            src="../../../public/headshots/warren_stokes.jpg"
                            alt=""
                        />
                        <h3
                            class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-900"
                        >
                            Warren Stokes
                        </h3>
                        <p class="text-sm leading-6 text-gray-600">
                            Accounts Manager
                        </p>
                    </li>
                </ul>
            </div>
        </main>
    </div>
</template>
